<template>
  <div class="task-list-view">
    <!-- Header -->
    <div class="flex justify-between items-center mb-3">
      <h1 class="text-2xl font-bold">Tasks</h1>
      <div class="flex ml-auto gap-2">
        <Button icon="pi pi-plus" label="Add Task" @click="addNewTask" />
      </div>
    </div>

    <!-- Tabs -->
    <div class="">
      <TabView v-model:activeIndex="activeTabIndex">
        <TabPanel value="0">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Overdue</span>
              <Badge :value="overdueCount" severity="danger" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="1">
          <template #header>
            <div class="flex items-center gap-2">
              <span>To Do</span>
              <Badge :value="todoCount" severity="info" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="2">
          <template #header>
            <div class="flex items-center gap-2">
              <span>In Progress</span>
              <Badge :value="doingCount" severity="warning" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="3">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Done</span>
              <Badge :value="doneCount" severity="success" />
            </div>
          </template>
        </TabPanel>
      </TabView>
    </div>

    <!-- Bulk Actions Card -->
    <Card v-if="hasSelectedTasks" class="mb-4">
      <template #content>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="pi pi-check-circle text-blue-600"></i>
            <span class="font-medium text-blue-800 dark:text-blue-200">
              {{ selectedTasks.size }} task{{ selectedTasks.size === 1 ? '' : 's' }} selected
            </span>
          </div>
          <div class="ml-6 flex items-center gap-2">
            <Button label="Reschedule 30d" size="small" outlined @click="rescheduleSelected(30)" />
            <Button label="Reschedule 90d" size="small" outlined @click="rescheduleSelected(90)" />
            <Button
              label="Reschedule 120d"
              size="small"
              outlined
              @click="rescheduleSelected(120)"
            />
            <Button label="Begin" size="small" severity="info" @click="beginSelected" />
            <Button label="Complete" size="small" severity="success" @click="completeSelected" />
            <Button label="Delete" size="small" severity="danger" @click="deleteSelected" />
            <Button icon="pi pi-times" size="small" text @click="clearSelection" />
          </div>
        </div>
      </template>
    </Card>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <ProgressSpinner />
    </div>

    <!-- Tasks List -->
    <div v-else class="space-y-4">
      <TaskCard
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        :readOnly="task.status === 'done'"
        :selected="selectedTasks.has(task.id!)"
        @select="toggleTaskSelection"
        @begin="handleBeginTask"
        @complete="handleCompleteTask"
        @edit="editTask"
        @delete="handleDeleteTask"
        @click:person="navigateToPerson"
        @click:organization="navigateToOrganization"
        class="my-1"
      />
    </div>

    <!-- Empty State -->
    <div v-if="!loading && tasks.length === 0" class="text-center py-8">
      <i class="pi pi-inbox text-4xl text-gray-400 mb-4"></i>
      <h3 class="text-lg font-medium text-gray-600 mb-2">No tasks found</h3>
      <p class="text-gray-500">Try adjusting your filters or create a new task.</p>
    </div>

    <!-- Load More Button -->
    <div v-if="!loading && tasks.length > 0 && hasMore" class="text-center mt-6">
      <Button label="Load More" icon="pi pi-refresh" outlined @click="loadMore" />
    </div>

    <!-- Task Dialog -->
    <TaskFormDialog
      v-model:visible="taskDialogVisible"
      :is-editing="isEditing"
      :task="taskToEdit"
      @save="saveTask"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useTaskStoreV2 } from '@/stores/v2/task'
import type { Task } from '@/types/Task'

// Components
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import TaskCard from '@/components/task/TaskCard.vue'
import TaskFormDialog from '@/components/task/TaskFormDialog.vue'

const router = useRouter()
const toast = useToast()
const taskStore = useTaskStoreV2()

// Tab state - default to "To Do" (index 1)
const activeTabIndex = ref<number>(1)

// Count state
const overdueCount = ref(0)
const todoCount = ref(0)
const doingCount = ref(0)
const doneCount = ref(0)

// State
const tasks = ref<Task[]>([])
const loading = ref(false)

// Selection state
const selectedTasks = ref<Set<string>>(new Set())

// Pagination state
const pageSize = ref(20)
const hasMore = ref(true)

// Task dialog
const taskDialogVisible = ref(false)
const isEditing = ref(false)
const taskToEdit = ref<Task | undefined>(undefined)

// Function to update counts
async function updateCounts() {
  try {
    const search = await taskStore.search()
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const [overdueResult, todoResult, doingResult, doneResult] = await Promise.all([
      search.overdue().count(),
      search.todo().count(),
      search.inProgress().count(),
      search.done().count(),
    ])

    overdueCount.value = overdueResult
    todoCount.value = todoResult
    doingCount.value = doingResult
    doneCount.value = doneResult
  } catch (error) {
    console.error('Error updating counts:', error)
  }
}

// Load tasks based on current tab
async function loadTasks() {
  loading.value = true
  try {
    const searchInstance = await taskStore.search()
    let searchQuery = searchInstance

    // Apply tab filter
    const tabIndex = activeTabIndex.value ?? 1
    switch (tabIndex) {
      case 0: // Overdue
        searchQuery = searchQuery.overdue()
        break
      case 1: // To Do
        searchQuery = searchQuery.todo()
        break
      case 2: // In Progress
        searchQuery = searchQuery.inProgress()
        break
      case 3: // Done
        searchQuery = searchQuery.done()
        break
      default:
        searchQuery = searchQuery.todo()
    }

    // Build spec and add limit, then run via firestore service
    // Note: We modify spec.limit after build() but before using it
    // since run() calls buildAndReset() which would reset our limit
    const spec = searchQuery.build()
    spec.limit = pageSize.value

    // Run the query using firestore service directly
    // Access the firestore through the search instance we already have
    const results = await searchInstance['firestore'].runQuery<Task>(spec)
    tasks.value = results
    hasMore.value = results.length === pageSize.value
  } catch (error) {
    console.error('Error loading tasks:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load tasks',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

// Selected tasks for bulk actions
const selectedTasksList = computed(() => {
  return tasks.value.filter((task) => selectedTasks.value.has(task.id!))
})

const hasSelectedTasks = computed(() => selectedTasks.value.size > 0)

// Watch for tab changes
watch(activeTabIndex, () => {
  clearSelection() // Clear selection when changing tabs
  loadTasks()
})

// Note: Removed the rawTasks watcher that was causing excessive count queries
// Counts are now only updated when tasks are actually modified (create/update/delete)

// Initialize
onMounted(async () => {
  await updateCounts()
  await nextTick()

  // Set default tab to the first one with items, or default to "To Do"
  if (overdueCount.value > 0) {
    activeTabIndex.value = 0 // Overdue
  } else if (todoCount.value > 0) {
    activeTabIndex.value = 1 // To Do
  } else if (doingCount.value > 0) {
    activeTabIndex.value = 2 // In Progress
  } else {
    activeTabIndex.value = 1 // Default to "To Do"
  }

  await loadTasks()
})

function loadMore() {
  pageSize.value += 20
  loadTasks()
}

// Task methods
function addNewTask() {
  isEditing.value = false
  taskToEdit.value = undefined
  taskDialogVisible.value = true
}

function viewTask(task: Task) {
  // For now, just edit the task. In the future, you might want a view-only mode
  editTask(task)
}

function editTask(task: Task) {
  isEditing.value = true
  taskToEdit.value = task
  taskDialogVisible.value = true
}

async function saveTask(taskData: Partial<Task>) {
  try {
    if (isEditing.value && taskToEdit.value?.id) {
      await taskStore.update(taskToEdit.value.id, taskData)
      toast.add({
        severity: 'success',
        summary: 'Task Updated',
        detail: 'Task has been updated successfully',
        life: 3000,
      })
    } else {
      // Ensure required fields are present
      if (!taskData.title) {
        console.error('Title is required')
        return
      }

      // Create a properly typed task data object
      const newTaskData = {
        ...taskData,
        tags: taskData.tags || [],
      }

      await taskStore.create(newTaskData)
      toast.add({
        severity: 'success',
        summary: 'Task Created',
        detail: 'Task has been created successfully',
        life: 3000,
      })
    }
    taskDialogVisible.value = false
    await updateCounts()
    await loadTasks()
  } catch (error) {
    console.error('Error saving task:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save task',
      life: 3000,
    })
  }
}

async function handleBeginTask(task: Task) {
  if (!task?.id) return

  try {
    await taskStore.update(task.id, { status: 'doing' })
    toast.add({
      severity: 'success',
      summary: 'Task Started',
      detail: `Task "${task.title}" has been started`,
      life: 3000,
    })
    await updateCounts()
    await loadTasks()
  } catch (error) {
    console.error('Error starting task:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to start task',
      life: 3000,
    })
  }
}

async function handleCompleteTask(task: Task) {
  if (!task?.id) return

  try {
    await taskStore.update(task.id, { status: 'done' })
    toast.add({
      severity: 'success',
      summary: 'Task Completed',
      detail: `Task "${task.title}" has been completed`,
      life: 3000,
    })
    await updateCounts()
    await loadTasks()
  } catch (error) {
    console.error('Error completing task:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to complete task',
      life: 3000,
    })
  }
}

async function handleDeleteTask(task: Task) {
  if (!task?.id) return

  try {
    await taskStore.remove(task.id)
    toast.add({
      severity: 'success',
      summary: 'Task Deleted',
      detail: `Task "${task.title}" has been deleted`,
      life: 3000,
    })
    await updateCounts()
    await loadTasks()
  } catch (error) {
    console.error('Error deleting task:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete task',
      life: 3000,
    })
  }
}

// Navigation methods
function navigateToPerson(personId?: string) {
  if (personId) {
    router.push(`/crm/people/${personId}`)
  }
}

function navigateToOrganization(organizationId?: string) {
  if (organizationId) {
    router.push(`/crm/organizations/${organizationId}`)
  }
}

// Selection methods
function toggleTaskSelection(taskId: string) {
  if (selectedTasks.value.has(taskId)) {
    selectedTasks.value.delete(taskId)
  } else {
    selectedTasks.value.add(taskId)
  }
}

function clearSelection() {
  selectedTasks.value.clear()
}

// Bulk action methods
async function rescheduleSelected(days: number) {
  if (selectedTasks.value.size === 0) return

  const newDueDate = new Date()
  newDueDate.setDate(newDueDate.getDate() + days)

  try {
    const updatePromises = Array.from(selectedTasks.value).map((taskId) =>
      taskStore.update(taskId, { dueAt: newDueDate }),
    )

    await Promise.all(updatePromises)

    toast.add({
      severity: 'success',
      summary: 'Tasks Rescheduled',
      detail: `${selectedTasks.value.size} task${selectedTasks.value.size === 1 ? '' : 's'} rescheduled to ${days} days from now`,
      life: 3000,
    })

    clearSelection()
    await updateCounts()
  } catch (error) {
    console.error('Error rescheduling tasks:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to reschedule tasks',
      life: 3000,
    })
  }
}

async function beginSelected() {
  if (selectedTasks.value.size === 0) return

  try {
    const updatePromises = Array.from(selectedTasks.value).map((taskId) =>
      taskStore.update(taskId, { status: 'doing' }),
    )

    await Promise.all(updatePromises)

    toast.add({
      severity: 'success',
      summary: 'Tasks Started',
      detail: `${selectedTasks.value.size} task${selectedTasks.value.size === 1 ? '' : 's'} started`,
      life: 3000,
    })

    clearSelection()
    await updateCounts()
  } catch (error) {
    console.error('Error starting tasks:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to start tasks',
      life: 3000,
    })
  }
}

async function completeSelected() {
  if (selectedTasks.value.size === 0) return

  try {
    const updatePromises = Array.from(selectedTasks.value).map((taskId) =>
      taskStore.update(taskId, { status: 'done' }),
    )

    await Promise.all(updatePromises)

    toast.add({
      severity: 'success',
      summary: 'Tasks Completed',
      detail: `${selectedTasks.value.size} task${selectedTasks.value.size === 1 ? '' : 's'} completed`,
      life: 3000,
    })

    clearSelection()
    await updateCounts()
  } catch (error) {
    console.error('Error completing tasks:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to complete tasks',
      life: 3000,
    })
  }
}

async function deleteSelected() {
  if (selectedTasks.value.size === 0) return

  try {
    const deletePromises = Array.from(selectedTasks.value).map((taskId) => taskStore.remove(taskId))

    await Promise.all(deletePromises)

    toast.add({
      severity: 'success',
      summary: 'Tasks Deleted',
      detail: `${selectedTasks.value.size} task${selectedTasks.value.size === 1 ? '' : 's'} deleted`,
      life: 3000,
    })

    clearSelection()
    await updateCounts()
  } catch (error) {
    console.error('Error deleting tasks:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete tasks',
      life: 3000,
    })
  }
}
</script>

<style scoped>
.task-list-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.p-card {
  border-radius: 0px;
}

/* Custom button styling for the outlined button */
:deep(.p-button-outlined) {
  border: 1px solid var(--p-primary-color);
  background: transparent;
  color: var(--p-primary-color);
  transition: all 0.2s ease-in-out;
}

:deep(.p-button-outlined:hover) {
  background: var(--p-primary-color);
  color: var(--p-primary-color-text);
  transform: scale(1.05);
}
</style>
